import type { MapRecord, MapSummary, Robot } from '../api/types';
import { t } from '../i18n';

export const fmt = (n: unknown, d = 2) => (typeof n === 'number' && Number.isFinite(n) ? n.toFixed(d) : '–');

export const timeStr = (t: number | string | Date = Date.now()) =>
  new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

export function ago(iso: string | undefined | null): string {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t('time.justNow');
  if (s < 3600) return t('time.minAgo', { n: Math.round(s / 60) });
  if (s < 86400) return t('time.hAgo', { n: Math.round(s / 3600) });
  return t('time.dAgo', { n: Math.round(s / 86400) });
}

export function bytes(n: number | undefined | null): string {
  if (typeof n !== 'number') return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export function duration(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ---- map geometry (PROTOCOL §6): x = origin[0] + px*res, y = origin[1] + (height - py)*res
type Geo = Pick<MapRecord | MapSummary, 'origin' | 'resolution' | 'height'>;

export function m2p(m: Geo, x: number, y: number): [number, number] {
  const ox = m.origin[0] ?? 0;
  const oy = m.origin[1] ?? 0;
  return [(x - ox) / m.resolution, m.height - (y - oy) / m.resolution];
}
export function p2m(m: Geo, px: number, py: number): [number, number] {
  const ox = m.origin[0] ?? 0;
  const oy = m.origin[1] ?? 0;
  return [ox + px * m.resolution, oy + (m.height - py) * m.resolution];
}
export const round2 = (n: number) => Math.round(n * 100) / 100;
export const normDeg = (d: number) => ((Math.round(d) % 360) + 360) % 360;

/** Short human description of what a robot is doing. */
export function robotTask(r: Robot): string {
  const st = r.status;
  if (!r.online) return '—';
  if (!st) return t('task.idle');
  if (st.emergencyStop) return t('task.estop');
  if (st.task) {
    const k = st.task.kind === 'tour' ? t('task.tour') : st.task.kind;
    const step = st.task.step !== undefined && st.task.total !== undefined ? ` · ${t('task.stepOf', { step: st.task.step, total: st.task.total })}` : '';
    return `${st.task.name ? `${k} “${st.task.name}”` : k}${step}${st.task.pointId ? ` · ${st.task.pointId}` : ''}`;
  }
  if (st.charging) return st.docked ? t('task.chargingDock') : t('task.charging');
  if (st.docked) return t('task.onDock');
  if (st.navigating) return t('task.navigating');
  if (st.driving) return t('task.driving');
  if (st.speech?.speaking) return t('task.speaking');
  if (st.speech?.listening) return t('task.listening');
  return t('task.idle');
}
